import { Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { BoardDialog } from '../board/BoardDialog';
import { BrassButton } from '../board/BrassButton';
import { COLOURS, displayStyle, uiStyle } from '../board/ui';
import { walletNotes } from '../../logic/slot/gemText';

/** The wallet board's art, looked up when it opens. */
export interface WalletBoardArt {
  floor: () => Texture | null;
  coin: () => Texture | null;
  gem: () => Texture | null;
}

/**
 * What gems and coins are for, on a board (producer 2026-10-14: explain
 * what everything does, no plain dialogs): each with its icon, the
 * player's count and a word on what it does, then where gems come from and
 * that coins never run out. GOT IT (or the dim) closes it.
 */
export class WalletBoard extends BoardDialog {
  private readonly gemIcon = new Sprite();
  private readonly coinIcon = new Sprite();
  private readonly gemLabel: Text;
  private readonly coinLabel: Text;
  private readonly gemCount: Text;
  private readonly coinCount: Text;
  private readonly gemLine: Text;
  private readonly coinLine: Text;
  private readonly rule = new Graphics();
  private readonly notes: Text;
  private readonly ok: BrassButton;
  private closed: (() => void) | null = null;
  private arranging = false;

  constructor(private readonly art: WalletBoardArt) {
    super({ title: 'YOUR WALLET', floor: art.floor(), maxWidth: 400, titleSize: 18 });
    for (const icon of [this.gemIcon, this.coinIcon]) icon.anchor.set(0.5);
    this.gemLabel = new Text({ text: 'GEMS', style: uiStyle(13, COLOURS.cream, '600') });
    this.coinLabel = new Text({ text: 'COINS', style: uiStyle(13, COLOURS.cream, '600') });
    this.gemCount = new Text({ text: '0', style: displayStyle(30) });
    this.coinCount = new Text({ text: '0', style: displayStyle(30) });
    this.gemLine = new Text({ text: 'Unlock new slots', style: uiStyle(14, COLOURS.cream, '500') });
    this.coinLine = new Text({ text: 'Free and endless', style: uiStyle(14, COLOURS.cream, '500') });
    for (const t of [this.gemLabel, this.coinLabel, this.gemCount, this.coinCount, this.gemLine, this.coinLine]) t.anchor.set(0.5, 0);
    this.notes = new Text({
      text: walletNotes(false),
      style: { ...uiStyle(15, COLOURS.cream, '500'), wordWrap: true, align: 'center', lineHeight: 20 },
    });
    this.notes.anchor.set(0.5, 0);
    this.ok = new BrassButton({ label: 'GOT IT', kind: 'gold', size: 17, onTap: () => void this.dismiss() });
    this.content.addChild(
      this.gemIcon, this.coinIcon, this.gemLabel, this.coinLabel, this.gemCount, this.coinCount,
      this.gemLine, this.coinLine, this.rule, this.notes, this.ok,
    );
    this.onDimTap = () => void this.dismiss();
  }

  /** The board as plain data, for the browser tests. */
  get snapshot(): { open: boolean; gems: string; coins: string; notes: string } {
    return { open: this.visible, gems: this.gemCount.text, coins: this.coinCount.text, notes: this.notes.text };
  }

  /** Show the wallet as it stands; resolves when it closes. `reelGems`:
   *  gems land on this player's reels (reel_gems). */
  async play(gems: number, coins: number, reelGems = false): Promise<void> {
    this.gemCount.text = gems.toLocaleString('en-GB');
    this.coinCount.text = coins.toLocaleString('en-GB');
    this.notes.text = walletNotes(reelGems);
    this.setFloor(this.art.floor());
    const gem = this.art.gem();
    const coin = this.art.coin();
    if (gem) this.gemIcon.texture = gem;
    if (coin) this.coinIcon.texture = coin;
    this.arrange();
    const closed = new Promise<void>((resolve) => (this.closed = resolve));
    await this.open();
    return closed;
  }

  layout(viewWidth: number, viewHeight: number): void {
    super.layout(viewWidth, viewHeight);
    this.arrange();
  }

  private async dismiss(): Promise<void> {
    const done = this.closed;
    if (!done) return;
    this.closed = null;
    await this.close();
    done();
  }

  private arrange(): void {
    if (this.arranging || !this.ok) return;
    this.arranging = true;
    const w = this.contentWidth;
    const cols = [w * 0.25, w * 0.75];
    const icon = 46;
    const place = (x: number, sprite: Sprite, label: Text, count: Text, line: Text) => {
      const k = icon / Math.max(1, sprite.texture.width, sprite.texture.height);
      sprite.scale.set(k);
      sprite.position.set(x, icon / 2 + 2);
      label.position.set(x, icon + 8);
      count.position.set(x, label.y + label.height);
      line.position.set(x, count.y + count.height);
    };
    place(cols[0]!, this.gemIcon, this.gemLabel, this.gemCount, this.gemLine);
    place(cols[1]!, this.coinIcon, this.coinLabel, this.coinCount, this.coinLine);
    const colBottom = this.coinLine.y + this.coinLine.height;
    // A brass rule between the two, and under them.
    this.rule
      .clear()
      .moveTo(w / 2, 6)
      .lineTo(w / 2, colBottom - 4)
      .stroke({ color: COLOURS.brass, alpha: 0.45, width: 1 })
      .moveTo(0, colBottom + 12)
      .lineTo(w, colBottom + 12)
      .stroke({ color: COLOURS.brass, alpha: 0.45, width: 1 });
    this.notes.style.wordWrapWidth = w - 8;
    this.notes.position.set(w / 2, colBottom + 24);
    const y = this.notes.y + this.notes.height + 18;
    const bw = Math.min(w, 200);
    this.ok.resize(bw, 46);
    this.ok.position.set((w - bw) / 2, y);
    this.setContentHeight(y + 50);
    this.arranging = false;
  }
}
